/**
 * Performance monitoring utility for tracking FPS and latency
 * Validates Requirements 4.1.1, 4.1.2, 4.1.3
 */

import { logger } from './logger';

export interface PerformanceMetrics {
  poseDetectionFps: number;
  renderingFps: number;
  poseLatency: number;
  frameTime: number;
  timestamp: number;
}

export interface PerformanceStats {
  average: number;
  min: number;
  max: number;
  current: number;
  samples: number;
}

export class PerformanceMonitor {
  private poseFrameCount = 0;
  private poseTimestamps: number[] = [];
  
  private renderFrameCount = 0;
  private renderTimestamps: number[] = [];
  
  private latencyMeasurements: number[] = [];
  private frameCaptureTimes = new Map<number, number>();
  
  private readonly maxSamples = 30; // Keep last 30 samples for rolling average (Requirements 7.4, 7.5)
  private readonly warningThreshold = 20; // FPS warning threshold (Requirement 7.2)
  
  private isMonitoring = false;
  private monitoringInterval: number | null = null;

  constructor() {
    this.reset();
  }

  /**
   * Start performance monitoring
   */
  start(): void {
    if (this.isMonitoring) {
      return;
    }

    this.reset();
    this.isMonitoring = true;

    // Log performance stats every 5 seconds
    this.monitoringInterval = window.setInterval(() => {
      this.logPerformanceStats();
    }, 5000);

    logger.log('Performance monitoring started');
  }

  /**
   * Stop performance monitoring
   */
  stop(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;
    
    if (this.monitoringInterval !== null) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }

    this.logFinalStats();
    logger.log('Performance monitoring stopped');
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.poseFrameCount = 0;
    this.poseTimestamps = [];
    
    this.renderFrameCount = 0;
    this.renderTimestamps = [];
    
    this.latencyMeasurements = [];
    this.frameCaptureTimes.clear();
  }

  /**
   * Record a pose detection frame
   * Call this when pose data is received
   */
  recordPoseFrame(frameId?: number): void {
    if (!this.isMonitoring) {
      return;
    }

    const now = performance.now();
    this.poseFrameCount++;
    this.poseTimestamps.push(now);

    // Keep only recent samples
    if (this.poseTimestamps.length > this.maxSamples) {
      this.poseTimestamps.shift();
    }

    // Calculate latency if frame capture time was recorded
    if (frameId !== undefined && this.frameCaptureTimes.has(frameId)) {
      const captureTime = this.frameCaptureTimes.get(frameId)!;
      const latency = now - captureTime;
      this.latencyMeasurements.push(latency);
      
      // Keep only recent samples
      if (this.latencyMeasurements.length > this.maxSamples) {
        this.latencyMeasurements.shift();
      }
      
      // Clean up old frame capture times
      this.frameCaptureTimes.delete(frameId);
    }
  }

  /**
   * Record when a frame is captured
   * Call this when video frame is captured for processing
   */
  recordFrameCapture(frameId: number): void {
    if (!this.isMonitoring) {
      return;
    }

    this.frameCaptureTimes.set(frameId, performance.now());

    // Clean up old entries (older than 1 second)
    const cutoff = performance.now() - 1000;
    for (const [id, time] of this.frameCaptureTimes.entries()) {
      if (time < cutoff) {
        this.frameCaptureTimes.delete(id);
      }
    }
  }

  /**
   * Record a render frame
   * Call this in the render loop
   */
  recordRenderFrame(): void {
    if (!this.isMonitoring) {
      return;
    }

    const now = performance.now();
    this.renderFrameCount++;
    this.renderTimestamps.push(now);

    // Keep only recent samples
    if (this.renderTimestamps.length > this.maxSamples) {
      this.renderTimestamps.shift();
    }
  }

  /**
   * Get current pose detection FPS
   */
  getPoseDetectionFps(): number {
    if (this.poseTimestamps.length < 2) {
      return 0;
    }

    const first = this.poseTimestamps[0];
    const last = this.poseTimestamps[this.poseTimestamps.length - 1];
    
    if (first === undefined || last === undefined) {
      return 0;
    }

    const timeSpan = last - first;
    return (this.poseTimestamps.length / timeSpan) * 1000;
  }

  /**
   * Get current rendering FPS
   */
  getRenderingFps(): number {
    if (this.renderTimestamps.length < 2) {
      return 0;
    }

    const first = this.renderTimestamps[0];
    const last = this.renderTimestamps[this.renderTimestamps.length - 1];
    
    if (first === undefined || last === undefined) {
      return 0;
    }

    const timeSpan = last - first;
    return (this.renderTimestamps.length / timeSpan) * 1000;
  }

  /**
   * Get rolling average FPS over last 30 frames
   * Validates Requirements 7.4, 7.5
   */
  getRollingAverageFps(): number {
    return this.getRenderingFps();
  }

  /**
   * Check if FPS warning should be displayed
   * Validates Requirement 7.2
   */
  shouldShowFpsWarning(): boolean {
    return this.getRollingAverageFps() < this.warningThreshold;
  }

  /**
   * Get average pose detection latency
   */
  getAverageLatency(): number {
    if (this.latencyMeasurements.length === 0) {
      return 0;
    }

    const sum = this.latencyMeasurements.reduce((a, b) => a + b, 0);
    return sum / this.latencyMeasurements.length;
  }

  /**
   * Get pose detection statistics
   */
  getPoseDetectionStats(): PerformanceStats {
    return this.calculateStats(this.poseTimestamps);
  }

  /**
   * Get rendering statistics
   */
  getRenderingStats(): PerformanceStats {
    return this.calculateStats(this.renderTimestamps);
  }

  /**
   * Get latency statistics
   */
  getLatencyStats(): { average: number; min: number; max: number; samples: number } {
    if (this.latencyMeasurements.length === 0) {
      return { average: 0, min: 0, max: 0, samples: 0 };
    }

    const sum = this.latencyMeasurements.reduce((a, b) => a + b, 0);
    return {
      average: sum / this.latencyMeasurements.length,
      min: Math.min(...this.latencyMeasurements),
      max: Math.max(...this.latencyMeasurements),
      samples: this.latencyMeasurements.length
    };
  }

  /**
   * Get current performance metrics
   */
  getCurrentMetrics(): PerformanceMetrics {
    const renderFps = this.getRenderingFps();
    const frameTime = renderFps > 0 ? 1000 / renderFps : 0;

    return {
      poseDetectionFps: this.getPoseDetectionFps(),
      renderingFps: renderFps,
      poseLatency: this.getAverageLatency(),
      frameTime: frameTime,
      timestamp: performance.now()
    };
  }

  /**
   * Check if performance meets minimum requirements
   */
  meetsRequirements(): boolean {
    const poseFps = this.getPoseDetectionFps();
    const renderFps = this.getRenderingFps();
    const latency = this.getAverageLatency();

    return (
      poseFps >= this.warningThreshold &&
      renderFps >= this.warningThreshold &&
      latency <= 100
    );
  }

  /**
   * Calculate statistics from timestamps
   */
  private calculateStats(timestamps: number[]): PerformanceStats {
    if (timestamps.length < 2) {
      return { average: 0, min: 0, max: 0, current: 0, samples: 0 };
    }

    // Calculate FPS for each consecutive pair
    const fpsSamples: number[] = [];
    for (let i = 1; i < timestamps.length; i++) {
      const prev = timestamps[i - 1];
      const curr = timestamps[i];
      
      if (prev !== undefined && curr !== undefined) {
        const delta = curr - prev;
        if (delta > 0) {
          fpsSamples.push(1000 / delta);
        }
      }
    }

    if (fpsSamples.length === 0) {
      return { average: 0, min: 0, max: 0, current: 0, samples: 0 };
    }

    const sum = fpsSamples.reduce((a, b) => a + b, 0);
    const lastSample = fpsSamples[fpsSamples.length - 1];
    
    return {
      average: sum / fpsSamples.length,
      min: Math.min(...fpsSamples),
      max: Math.max(...fpsSamples),
      current: lastSample !== undefined ? lastSample : 0,
      samples: fpsSamples.length
    };
  }

  /**
   * Log current performance stats to console
   */
  private logPerformanceStats(): void {
    const metrics = this.getCurrentMetrics();
    const poseStats = this.getPoseDetectionStats();
    const renderStats = this.getRenderingStats();
    const latencyStats = this.getLatencyStats();

    logger.group('📊 Performance Metrics', () => {
      logger.log(`Pose Detection: ${metrics.poseDetectionFps.toFixed(1)} fps (avg: ${poseStats.average.toFixed(1)}, min: ${poseStats.min.toFixed(1)}, max: ${poseStats.max.toFixed(1)})`);
      logger.log(`Rendering: ${metrics.renderingFps.toFixed(1)} fps (avg: ${renderStats.average.toFixed(1)}, min: ${renderStats.min.toFixed(1)}, max: ${renderStats.max.toFixed(1)})`);
      logger.log(`Latency: ${metrics.poseLatency.toFixed(1)}ms (avg: ${latencyStats.average.toFixed(1)}, min: ${latencyStats.min.toFixed(1)}, max: ${latencyStats.max.toFixed(1)})`);
      logger.log(`Frame Time: ${metrics.frameTime.toFixed(1)}ms`);

      if (!this.meetsRequirements()) {
        logger.warn('⚠️ Performance below minimum requirements (20fps, 100ms latency)');
      } else {
        logger.log('✓ Performance meets requirements');
      }
    });
  }

  /**
   * Log final statistics when monitoring stops
   */
  private logFinalStats(): void {
    const poseStats = this.getPoseDetectionStats();
    const renderStats = this.getRenderingStats();
    const latencyStats = this.getLatencyStats();

    logger.group('📈 Final Performance Report', () => {
      logger.log('=== Pose Detection ===');
      logger.log(`Average FPS: ${poseStats.average.toFixed(2)}`);
      logger.log(`Min FPS: ${poseStats.min.toFixed(2)}`);
      logger.log(`Max FPS: ${poseStats.max.toFixed(2)}`);
      logger.log(`Total Frames: ${this.poseFrameCount}`);
      logger.log(`Meets 20fps requirement: ${poseStats.average >= 20 ? '✓ YES' : '✗ NO'}`);

      logger.log('\n=== Rendering ===');
      logger.log(`Average FPS: ${renderStats.average.toFixed(2)}`);
      logger.log(`Min FPS: ${renderStats.min.toFixed(2)}`);
      logger.log(`Max FPS: ${renderStats.max.toFixed(2)}`);
      logger.log(`Total Frames: ${this.renderFrameCount}`);
      logger.log(`Meets 20fps requirement: ${renderStats.average >= 20 ? '✓ YES' : '✗ NO'}`);

      logger.log('\n=== Latency ===');
      logger.log(`Average: ${latencyStats.average.toFixed(2)}ms`);
      logger.log(`Min: ${latencyStats.min.toFixed(2)}ms`);
      logger.log(`Max: ${latencyStats.max.toFixed(2)}ms`);
      logger.log(`Samples: ${latencyStats.samples}`);
      logger.log(`Meets 100ms requirement: ${latencyStats.average <= 100 ? '✓ YES' : '✗ NO'}`);

      logger.log('\n=== Overall ===');
      logger.log(`Performance Grade: ${this.getPerformanceGrade()}`);
    });
  }

  /**
   * Get performance grade
   */
  private getPerformanceGrade(): string {
    const poseStats = this.getPoseDetectionStats();
    const renderStats = this.getRenderingStats();
    const latencyStats = this.getLatencyStats();

    const poseFps = poseStats.average;
    const renderFps = renderStats.average;
    const avgLatency = latencyStats.average;

    if (poseFps >= 30 && renderFps >= 30 && avgLatency <= 50) {
      return 'A (Excellent)';
    } else if (poseFps >= 25 && renderFps >= 25 && avgLatency <= 75) {
      return 'B (Good)';
    } else if (poseFps >= 20 && renderFps >= 20 && avgLatency <= 100) {
      return 'C (Acceptable)';
    } else if (poseFps >= 15 && renderFps >= 15 && avgLatency <= 150) {
      return 'D (Below Target)';
    } else {
      return 'F (Unacceptable)';
    }
  }
}

// Create global instance for easy access in console
if (typeof window !== 'undefined') {
  (window as any).performanceMonitor = new PerformanceMonitor();
}
